/**
 * W1-11b (app-shell-ui): transport and time helpers behind the shell's `playback.*`, `nav.*` and `snap.*` commands,
 * the transport readout and the `@` palette provider. Musical positions come from the document's tempo map
 * (core/time, Phira-exact) and meter (bar lines); the playhead is the audio transport's chart time.
 */

import type { Beat, Sec, TempoMap } from '../../core/time/types';
import type { ChartDoc, KeyId, LineId, TrackId } from '../../core/model/types';
import type { DocStore } from '../../state/types';
import { Beats } from '../../core/time/beat';
import { createTempoMap } from '../../core/time/tempo';
import { createMeterMap, type MeterMap } from '../../core/time/meter';
import { barBeatRefForMeter, formatBeat } from '../../core/time/beatSyntax';
import type { MessageRef } from '../../core/util/types';
import type { UserSettings } from '../settings/types';
import type { AppServices } from '../registry/types';

/** Musical snap presets (Shift+1..9, the snap menu, cycling). */
export const SNAP_PRESETS: readonly number[] = [1, 2, 3, 4, 6, 8, 12, 16, 24, 32];
/** Divisors of Shift+Digit1..9. */
export const SNAP_DIGIT_DIVS: readonly number[] = [1, 2, 3, 4, 6, 8, 12, 16, 32];
/** Playback rate presets (rate menu, rate up / down). */
export const RATE_PRESETS: readonly number[] = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2];

/** A document with its store (the store's version counters key the caches: its tables are live Maps). */
type DocSource = Pick<DocStore, 'doc' | 'version'>;

const tempoCache = new WeakMap<object, { readonly v: number; readonly map: TempoMap }>();
const meterCache = new WeakMap<object, { readonly timing: ChartDoc['timing']; readonly map: MeterMap }>();

/** The document's tempo map (cached until the BPM table changes). */
export function tempoOf(store: DocSource): TempoMap {
  const v = store.version('bpm');
  const hit = tempoCache.get(store);
  if (hit?.v === v) return hit.map;
  const points = [...store.doc.bpm.values()].sort((a, b) => Beats.cmp(a.t, b.t));
  const map = createTempoMap(points.map((p) => ({ bpm: p.bpm, startTime: Beats.toTriple(p.t) })));
  tempoCache.set(store, { v, map });
  return map;
}

/** The document's meter (bar lines; cached per timing record, which is immutable). */
export function meterOf(store: DocSource): MeterMap {
  const timing = store.doc.timing;
  const hit = meterCache.get(store);
  if (hit?.timing === timing) return hit.map;
  const map = createMeterMap(timing.meter);
  meterCache.set(store, { timing, map });
  return map;
}

export type BeatDisplay = UserSettings['editing']['beatDisplay'];

/**
 * A beat as the settings show it: 'colon' `12:3/4`, 'plus' `12 + 3/4`, or 'bar' "bar 4 . beat 1 + 1/4" counted under
 * the document's meter (bars restart at every meter mark; translated through `tr`).
 */
export function displayBeat(
  b: Beat,
  style: BeatDisplay,
  store: DocSource | null,
  tr: (r: MessageRef) => string,
): string {
  if (style !== 'bar') return formatBeat(b, style);
  return store === null ? tr(barBeatRefForMeter(b, [])) : tr(barBeatRefForMeter(b, meterOf(store)));
}

/** A Beat as command JSON (`BeatArg`; a type literal, so it is also `Json`). */
export function beatArg(b: Beat): { readonly w: number; readonly n: number; readonly d: number } {
  return { w: b.w, n: b.n, d: b.d };
}

/** A BeatArg from JSON (null when malformed). */
export function beatFromArg(v: unknown): Beat | null {
  if (v === null || typeof v !== 'object') return null;
  const o = v as Readonly<Record<string, unknown>>;
  const { w, n, d } = o;
  if (typeof w !== 'number' || typeof n !== 'number' || typeof d !== 'number') return null;
  try {
    return Beats.make(w, n, d);
  } catch {
    return null;
  }
}

/** Current playhead in seconds. */
export function playheadSec(s: AppServices): Sec {
  try {
    return s.audio.transport.chartTime();
  } catch {
    return 0;
  }
}

/** Current playhead as an f64 beat (0 without a project). */
export function playheadBeat(s: AppServices): number {
  const store = s.store;
  if (store === null) return 0;
  return tempoOf(store).beatF64At(playheadSec(s));
}

/** End of the chart in seconds: the music length, else the last note or keyframe. */
export function chartEndSec(s: AppServices): Sec {
  const music = s.audio.music;
  if (music !== null && music.durationSec > 0) return music.durationSec;
  const store = s.store;
  if (store === null) return 0;
  const tempo = tempoOf(store);
  let last = 0;
  for (const n of store.doc.notes.values()) last = Math.max(last, tempo.secAt(n.end));
  for (const k of store.doc.keys.values()) last = Math.max(last, tempo.secAt(k.t));
  return last;
}

/** Seek the transport to a second (clamped to 0) and keep the timeline on it. */
export function seekSec(s: AppServices, sec: Sec, reveal = true): void {
  const t = Math.max(0, sec);
  s.audio.transport.seek(t);
  if (!reveal || s.store === null) return;
  const beat = tempoOf(s.store).beatF64At(t);
  try {
    s.timeline?.reveal(beat, 'playhead');
  } catch {
    // The timeline may be mid-mount; the playhead still moved.
  }
}

/** Seek to an f64 beat. */
export function seekBeat(s: AppServices, beat: number, reveal = true): void {
  if (s.store === null) return;
  seekSec(s, tempoOf(s.store).secAtBeatF64(beat), reveal);
}

const EPS = 1e-6;

/** The next (dir 1) or previous (-1) multiple of 1/div from `beat` (strictly past it). */
export function snapStep(beat: number, div: number, dir: 1 | -1): number {
  const d = Math.max(1, Math.round(div));
  const pos = beat * d;
  const k = dir === 1 ? Math.floor(pos + EPS) + 1 : Math.ceil(pos - EPS) - 1;
  return k / d;
}

/** Nearest multiple of 1/div. */
export function snapNearest(beat: number, div: number): number {
  const d = Math.max(1, Math.round(div));
  return Math.round(beat * d) / d;
}

/** The next / previous bar line from an f64 beat, under the document's meter. */
export function barStep(store: DocSource, beat: number, dir: 1 | -1): number {
  const mm = meterOf(store);
  // The nearest 1/1920 beat: a playhead within float noise of a bar line counts as on it.
  const b = Beats.fromF64(Math.max(0, beat), 1920);
  return Beats.toF64(dir === 1 ? mm.nextBarStart(b) : mm.prevBarStart(b));
}

/** Beat times (seconds) with accents on bar lines, for the metronome over [t0, t1). */
export function metronomeBeats(
  store: DocSource,
): (t0: Sec, t1: Sec) => ReadonlyArray<{ readonly t: Sec; readonly accent: boolean }> {
  const tempo = tempoOf(store);
  const mm = meterOf(store);
  return (t0, t1) => {
    const out: Array<{ readonly t: Sec; readonly accent: boolean }> = [];
    if (!(t1 > t0)) return out;
    const b0 = Math.ceil(tempo.beatF64At(Math.max(0, t0)) - EPS);
    const b1 = tempo.beatF64At(t1);
    for (let b = Math.max(0, b0); b < b1 && out.length < 4096; b++) {
      const t = tempo.secAtBeatF64(b);
      if (t < t0 || t >= t1) continue;
      out.push({ t, accent: mm.isBarStart(Beats.fromInt(b)) });
    }
    return out;
  };
}

/** Beat range [start, end] (f64) of the current selection's notes, keys and segments; null when empty. */
export function selectionRange(s: AppServices): { readonly start: number; readonly end: number } | null {
  const store = s.store;
  if (store === null) return null;
  const sel = s.editor.getState().selection;
  let lo = Infinity;
  let hi = -Infinity;
  const take = (a: Beat, b: Beat = a): void => {
    lo = Math.min(lo, Beats.toF64(a));
    hi = Math.max(hi, Beats.toF64(b));
  };
  for (const id of sel.notes) {
    const n = store.doc.notes.get(id);
    if (n !== undefined) take(n.t, n.end);
  }
  const keyIds = new Set<KeyId>([...sel.keys, ...sel.segments]);
  for (const id of keyIds) {
    const k = store.doc.keys.get(id);
    if (k === undefined) continue;
    take(k.t);
    if (sel.segments.has(id)) {
      const next = store.idx.nextKey(id);
      const nk = next === undefined ? undefined : store.doc.keys.get(next);
      if (nk !== undefined) take(nk.t);
    }
  }
  for (const id of sel.markers) {
    const mk = store.doc.markers.get(id);
    if (mk !== undefined) take(mk.t);
  }
  return Number.isFinite(lo) ? { start: lo, end: hi } : null;
}

/** Beats (f64, sorted) of a line's notes. */
export function noteBeats(s: AppServices, line: LineId): number[] {
  const store = s.store;
  if (store === null) return [];
  const out: number[] = [];
  for (const id of store.idx.notesByLine(line)) {
    const n = store.doc.notes.get(id);
    if (n !== undefined) out.push(Beats.toF64(n.t));
  }
  return out.sort((a, b) => a - b);
}

/** Beats (f64, sorted, unique) of the keys of the given tracks. */
export function keyBeatsOf(s: AppServices, tracks: Iterable<TrackId>): number[] {
  const store = s.store;
  if (store === null) return [];
  const set = new Set<number>();
  for (const track of tracks) {
    for (const id of store.idx.keysByTrack(track)) {
      const k = store.doc.keys.get(id);
      if (k !== undefined) set.add(Beats.toF64(k.t));
    }
  }
  return [...set].sort((a, b) => a - b);
}

/** First value strictly after (dir 1) / before (dir -1) `beat` in a sorted list, or null. */
export function neighbour(sorted: readonly number[], beat: number, dir: 1 | -1): number | null {
  if (dir === 1) {
    for (const b of sorted) if (b > beat + EPS) return b;
    return null;
  }
  for (let i = sorted.length - 1; i >= 0; i--) {
    const b = sorted[i];
    if (b !== undefined && b < beat - EPS) return b;
  }
  return null;
}

/** The next musical snap preset (dir 1 = finer) from a divisor. */
export function cycleSnap(div: number, dir: 1 | -1): number {
  if (dir === 1) return SNAP_PRESETS.find((p) => p > div) ?? (SNAP_PRESETS[SNAP_PRESETS.length - 1] as number);
  for (let i = SNAP_PRESETS.length - 1; i >= 0; i--) {
    const p = SNAP_PRESETS[i];
    if (p !== undefined && p < div) return p;
  }
  return SNAP_PRESETS[0] as number;
}

/** The next rate preset up / down from a rate. */
export function stepRate(rate: number, dir: 1 | -1): number {
  if (dir === 1) return RATE_PRESETS.find((r) => r > rate + 1e-9) ?? (RATE_PRESETS[RATE_PRESETS.length - 1] as number);
  for (let i = RATE_PRESETS.length - 1; i >= 0; i--) {
    const r = RATE_PRESETS[i];
    if (r !== undefined && r < rate - 1e-9) return r;
  }
  return RATE_PRESETS[0] as number;
}

/** Rate as shown in the transport ("1.00x" style number part). */
export function formatRate(rate: number): string {
  return rate.toFixed(2);
}
